import { z } from 'zod';
import { webhookDeliveryStatusSchema, webhookDisabledReasonSchema, webhookEventsSchema, webhookEventTypeSchema, webhookUrlSchema, } from '../../integrations.js';
/**
 * A website's integrations (`features/integrations`): webhook endpoints its
 * leads are sent to. Every route needs the integrations permission. Adding,
 * turning on and testing an endpoint need a plan with integrations
 * (`RULE_BLOCKED PLAN_EXCLUDES_INTEGRATIONS`).
 */
const websiteParams = z.object({ websiteId: z.uuid() });
const endpointParams = websiteParams.extend({ webhookEndpointId: z.uuid() });
const deliveryParams = endpointParams.extend({ deliveryId: z.uuid() });
export const webhookDeliverySummarySchema = z.object({
    status: webhookDeliveryStatusSchema,
    createdAt: z.iso.datetime(),
    lastStatusCode: z.number().int().nullable(),
});
export const webhookEndpointSchema = z.object({
    id: z.uuid(),
    url: z.string(),
    description: z.string().nullable(),
    events: z.array(webhookEventTypeSchema),
    isEnabled: z.boolean(),
    /** Set when Groundly turned it off by itself; null when it's on or a person turned it off. */
    disabledReason: webhookDisabledReasonSchema.nullable(),
    /** The first characters of the signing secret, to tell secrets apart. */
    secretHint: z.string(),
    lastDelivery: webhookDeliverySummarySchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const listWebhookEndpointsResponseSchema = z.object({
    items: z.array(webhookEndpointSchema),
    /** The website's plan this cycle has integrations. */
    integrationsAllowed: z.boolean(),
    limit: z.number().int(),
});
/** The signing secret is shown only here, once: when created and when rotated. */
export const webhookEndpointWithSecretSchema = webhookEndpointSchema.extend({
    secret: z.string(),
});
export const createWebhookEndpointBodySchema = z.object({
    url: webhookUrlSchema,
    description: z
        .string()
        .trim()
        .max(200)
        .nullable()
        .default(null)
        .transform((value) => (value === '' ? null : value)),
    events: webhookEventsSchema,
});
export const updateWebhookEndpointBodySchema = z
    .object({
    url: webhookUrlSchema,
    description: z
        .string()
        .trim()
        .max(200)
        .nullable()
        .transform((value) => (value === '' ? null : value)),
    events: webhookEventsSchema,
    isEnabled: z.boolean(),
})
    .partial();
export const webhookTestResultSchema = z.object({
    ok: z.boolean(),
    statusCode: z.number().int().nullable(),
    durationMs: z.number().int(),
    /** Why it failed, in plain words; null when it worked. */
    error: z.string().nullable(),
});
export const webhookDeliverySchema = z.object({
    id: z.uuid(),
    eventId: z.uuid(),
    eventType: webhookEventTypeSchema,
    status: webhookDeliveryStatusSchema,
    attemptCount: z.number().int(),
    nextAttemptAt: z.iso.datetime().nullable(),
    lastStatusCode: z.number().int().nullable(),
    lastError: z.string().nullable(),
    lastDurationMs: z.number().int().nullable(),
    deliveredAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const listWebhookDeliveriesResponseSchema = z.object({
    items: z.array(webhookDeliverySchema),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
});
/** Oldest first. No pagination: a website has at most `MAX_WEBHOOK_ENDPOINTS`. */
export const listWebhookEndpoints = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: listWebhookEndpointsResponseSchema,
};
/** `201`. `RULE_BLOCKED` `PLAN_EXCLUDES_INTEGRATIONS`, `WEBHOOK_ENDPOINT_LIMIT`. */
export const createWebhookEndpoint = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints',
    params: websiteParams,
    query: z.object({}),
    body: createWebhookEndpointBodySchema,
    response: webhookEndpointWithSecretSchema,
};
/** Turning it on needs a plan with integrations, and starts its failure count again. */
export const updateWebhookEndpoint = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId',
    params: endpointParams,
    query: z.object({}),
    body: updateWebhookEndpointBodySchema,
    response: webhookEndpointSchema,
};
/** `204`. Its deliveries go with it. */
export const deleteWebhookEndpoint = {
    method: 'DELETE',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId',
    params: endpointParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
/** `201`. A new signing secret; the old one stops working at once. */
export const rotateWebhookSecret = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId/secret-rotations',
    params: endpointParams,
    query: z.object({}),
    body: z.object({}),
    response: webhookEndpointWithSecretSchema,
};
/**
 * `200`. Sends a `webhook.test` event with sample lead data now and answers
 * with what the endpoint said. Nothing is stored or retried.
 */
export const testWebhookEndpoint = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId/tests',
    params: endpointParams,
    query: z.object({}),
    body: z.object({}),
    response: webhookTestResultSchema,
};
/** Newest first. */
export const listWebhookDeliveries = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId/deliveries',
    params: endpointParams,
    query: z.object({
        page: z.coerce.number().int().min(1).default(1),
        pageSize: z.coerce.number().int().min(1).max(100).default(50),
    }),
    body: z.object({}),
    response: listWebhookDeliveriesResponseSchema,
};
/** `202`. Sends a finished delivery again, as a new set of attempts. */
export const redeliverWebhook = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/webhook-endpoints/:webhookEndpointId/deliveries/:deliveryId/redeliveries',
    params: deliveryParams,
    query: z.object({}),
    body: z.object({}),
    response: webhookDeliverySchema,
};
/**
 * Slack (`features/integrations`): a website's leads posted to Slack
 * channels. A channel is connected through Slack's "Add to Slack" approval;
 * the address Slack gives back is kept sealed and never returned. A
 * connection's deliveries are listed and sent again with the webhook routes
 * above, by its id.
 */
const slackConnectionParams = websiteParams.extend({ slackConnectionId: z.uuid() });
export const slackConnectionSchema = z.object({
    id: z.uuid(),
    /** The Slack workspace and channel, as Slack named them when it was connected. */
    teamName: z.string(),
    channelName: z.string(),
    events: z.array(webhookEventTypeSchema),
    isEnabled: z.boolean(),
    /** `failing` or `revoked` when Groundly turned it off; null otherwise. */
    disabledReason: webhookDisabledReasonSchema.nullable(),
    lastDelivery: webhookDeliverySummarySchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const listSlackConnectionsResponseSchema = z.object({
    items: z.array(slackConnectionSchema),
    integrationsAllowed: z.boolean(),
    /** This Groundly has a Slack app to connect with (`SLACK_CLIENT_ID`). */
    slackAvailable: z.boolean(),
    limit: z.number().int(),
});
/** Oldest first. */
export const listSlackConnections = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/slack-connections',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: listSlackConnectionsResponseSchema,
};
export const slackInstallStartedSchema = z.object({
    /** Slack's approval page; the dashboard sends the browser there. */
    authorizeUrl: z.url(),
});
/**
 * `201`. Starts "Add to Slack": where to send the browser, with a state that
 * ties Slack's answer to this website and Account User for 10 minutes.
 * `RULE_BLOCKED` `PLAN_EXCLUDES_INTEGRATIONS`, `SLACK_CONNECTION_LIMIT`,
 * `SLACK_NOT_CONFIGURED`.
 */
export const startSlackInstall = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/slack-installs',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: slackInstallStartedSchema,
};
export const completeSlackInstallBodySchema = z.object({
    code: z.string().trim().min(1).max(500),
    state: z.string().trim().min(1).max(2_000),
});
export const slackInstallCompletedSchema = z.object({
    websiteId: z.uuid(),
    connection: slackConnectionSchema,
});
/**
 * `201`. Finishes "Add to Slack" with what Slack sent back to the dashboard.
 * The website comes from the state, which must have been made for this
 * Account User in the last 10 minutes: otherwise `VALIDATION_FAILED`.
 */
export const completeSlackInstall = {
    method: 'POST',
    path: '/api/dashboard/slack-installs/completions',
    params: z.object({}),
    query: z.object({}),
    body: completeSlackInstallBodySchema,
    response: slackInstallCompletedSchema,
};
export const updateSlackConnectionBodySchema = z
    .object({ events: webhookEventsSchema, isEnabled: z.boolean() })
    .partial();
/** Turning it on needs a plan with integrations; a `revoked` one must be connected again. */
export const updateSlackConnection = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/slack-connections/:slackConnectionId',
    params: slackConnectionParams,
    query: z.object({}),
    body: updateSlackConnectionBodySchema,
    response: slackConnectionSchema,
};
/** `204`. Groundly stops posting; the app stays in Slack until removed there. */
export const deleteSlackConnection = {
    method: 'DELETE',
    path: '/api/dashboard/websites/:websiteId/slack-connections/:slackConnectionId',
    params: slackConnectionParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
/** `200`. Posts a sample lead to the channel now. Nothing is stored or retried. */
export const testSlackConnection = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/slack-connections/:slackConnectionId/tests',
    params: slackConnectionParams,
    query: z.object({}),
    body: z.object({}),
    response: webhookTestResultSchema,
};
/**
 * CRM integrations (`features/integrations`): HubSpot, Salesforce and Zoho CRM.
 * A CRM connection is an endpoint of its kind (`hubspot`, `salesforce`, `zoho`);
 * leads reach it through the same events, deliveries and retries as webhooks.
 * Tokens are sealed and never returned to the dashboard.
 */
const crmConnectionParams = websiteParams.extend({ crmConnectionId: z.uuid() });
export const crmConnectionSchema = z.object({
    id: z.uuid(),
    kind: z.enum(['hubspot', 'salesforce', 'zoho']),
    /** The CRM account name, as the provider named it when connected. */
    accountName: z.string(),
    /** A link into the CRM (the portal or contacts page). */
    portalUrl: z.string(),
    events: z.array(webhookEventTypeSchema),
    isEnabled: z.boolean(),
    disabledReason: webhookDisabledReasonSchema.nullable(),
    lastDelivery: webhookDeliverySummarySchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const listCrmConnectionsResponseSchema = z.object({
    items: z.array(crmConnectionSchema),
    integrationsAllowed: z.boolean(),
    /** This Groundly has the CRM app configured (`<CRM>_CLIENT_ID`). */
    crmAvailable: z.boolean(),
    limit: z.number().int(),
});
export const crmInstallStartedSchema = z.object({
    authorizeUrl: z.url(),
});
export const completeCrmInstallBodySchema = z.object({
    code: z.string().trim().min(1).max(4_000),
    state: z.string().trim().min(1).max(4_000),
});
export const crmInstallCompletedSchema = z.object({
    websiteId: z.uuid(),
    connection: crmConnectionSchema,
});
export const updateCrmConnectionBodySchema = z
    .object({ events: webhookEventsSchema, isEnabled: z.boolean() })
    .partial();
/** Typed route builders — one set per CRM, same shapes. */
function crmRoutes(crm) {
    const prefix = `/api/dashboard/websites/:websiteId/${crm}-connections`;
    return {
        list: {
            method: 'GET',
            path: prefix,
            params: websiteParams,
            query: z.object({}),
            body: z.object({}),
            response: listCrmConnectionsResponseSchema,
        },
        startInstall: {
            method: 'POST',
            path: `${prefix}/installs`,
            params: websiteParams,
            query: z.object({}),
            body: z.object({}),
            response: crmInstallStartedSchema,
        },
        completeInstall: {
            method: 'POST',
            path: `/api/dashboard/${crm}-installs/completions`,
            params: z.object({}),
            query: z.object({}),
            body: completeCrmInstallBodySchema,
            response: crmInstallCompletedSchema,
        },
        update: {
            method: 'PATCH',
            path: `${prefix}/:crmConnectionId`,
            params: crmConnectionParams,
            query: z.object({}),
            body: updateCrmConnectionBodySchema,
            response: crmConnectionSchema,
        },
        delete: {
            method: 'DELETE',
            path: `${prefix}/:crmConnectionId`,
            params: crmConnectionParams,
            query: z.object({}),
            body: z.object({}),
            response: z.void(),
        },
        test: {
            method: 'POST',
            path: `${prefix}/:crmConnectionId/tests`,
            params: crmConnectionParams,
            query: z.object({}),
            body: z.object({}),
            response: webhookTestResultSchema,
        },
    };
}
export const hubspotRoutes = crmRoutes('hubspot');
export const salesforceRoutes = crmRoutes('salesforce');
export const zohoRoutes = crmRoutes('zoho');
//# sourceMappingURL=integrations.js.map